import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronRight, MapPin, Maximize2, Home as HomeIcon, Star, CheckCircle2, Phone, Mail, ChevronLeft, X, BedDouble, MessageCircle, Share2 } from 'lucide-react';
import Button from '../components/ui/Button';
import { AGENCY_INFO, formatPrice } from '../constants';
import ShareModal from '../components/ShareModal';
import { fetchPropertyById, getAgentDisplayInfo, formatNettyRef } from '../lib/nettyService';
import { MOCK_PROPERTIES } from '../data/mocks';
import { trackContactClick, trackPropertyView, trackPageView } from '../lib/analytics';
import { getOptimizedImageUrl } from '../lib/imageOptimizer';
import { TEAM_MEMBERS } from '../data/team';

const SITE_TARGET = 'avantis';

const PropertyDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [property, setProperty] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState(null);
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);

    // Dynamic agent info from Netty
    const agent = getAgentDisplayInfo(property);
    const agentMatch = TEAM_MEMBERS.find(m => 
        agent?.name && m.name.toLowerCase().includes(agent.name.split(' ')[0].toLowerCase())
    );
    const agentPhoto = agentMatch?.image || agent?.photo || (property?.site_target === 'vanessa' ? '/photo_vanessa.jpg' : AGENCY_INFO.photo);

    // Lightbox Logic
    const allImages = property ? [property.image_url || property.image, ...(property.images || [])].filter(Boolean) : [];
    const currentImageSrc = selectedImage || property?.image_url || property?.image;
    const currentIndex = allImages.indexOf(currentImageSrc);

    const nextImage = (e) => {
        e?.stopPropagation();
        const nextIdx = (currentIndex + 1) % allImages.length;
        setSelectedImage(allImages[nextIdx]);
    };

    const prevImage = (e) => {
        e?.stopPropagation();
        const prevIdx = (currentIndex - 1 + allImages.length) % allImages.length;
        setSelectedImage(allImages[prevIdx]);
    };

    const openLightbox = () => {
        setIsLightboxOpen(true);
        try {
            const elem = document.documentElement;
            if (elem.requestFullscreen) elem.requestFullscreen().catch(() => { });
            else if (elem.webkitRequestFullscreen) elem.webkitRequestFullscreen();
        } catch (e) { }
    };

    const closeLightbox = () => {
        setIsLightboxOpen(false);
        try {
            if (document.exitFullscreen) document.exitFullscreen().catch(() => { });
        } catch (e) { }
    };

    useEffect(() => {
        const fetchProperty = async () => {
            setLoading(true);
            try {
                const data = await fetchPropertyById(id, SITE_TARGET);
                if (data) {
                    setProperty(data);
                } else {
                    const mock = MOCK_PROPERTIES.find(p => p.id === id);
                    if (mock) setProperty(mock);
                }
            } catch (e) {
                const mock = MOCK_PROPERTIES.find(p => p.id === id);
                if (mock) setProperty(mock);
            } finally {
                setLoading(false);
            }
        };
        fetchProperty();
        window.scrollTo(0, 0);
    }, [id]);

    useEffect(() => {
        if (property) {
            const fullTitle = `${property.title} à ${property.city} - Avantis Immobilier`;
            document.title = fullTitle;
            trackPropertyView(property);
            trackPageView(window.location.hash, fullTitle);
        }
    }, [property]);

    if (loading) return <div className="p-20 text-center">Chargement...</div>;
    if (!property) return <div className="p-20 text-center">Bien introuvable.</div>;

    return (
        <div className="bg-[#F7F7F7] min-h-screen pb-20 font-sans relative">
            {/* Lightbox Overlay */}
            {isLightboxOpen && (
                <div className="fixed inset-0 z-[100] bg-black/95 flex flex-col md:flex-row items-center justify-center p-4 backdrop-blur-sm" onClick={closeLightbox}>
                    <button onClick={closeLightbox} className="absolute top-6 right-6 text-white/50 hover:text-white transition-colors p-2 z-[101]">
                        <X size={32} />
                    </button>
                    <button onClick={prevImage} className="hidden sm:block absolute left-6 text-white/50 hover:text-white transition-colors p-4 z-[101]">
                        <ChevronLeft size={48} />
                    </button>
                    <img
                        src={currentImageSrc}
                        alt="Plein écran"
                        className="max-w-full max-h-[60vh] sm:max-h-[85vh] md:max-h-[90vh] object-contain rounded-lg shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    />
                    <button onClick={nextImage} className="hidden sm:block absolute right-6 text-white/50 hover:text-white transition-colors p-4 z-[101]">
                        <ChevronRight size={48} />
                    </button>
                    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/50 text-sm font-medium tracking-widest uppercase">
                        {currentIndex + 1} / {allImages.length}
                    </div>
                </div>
            )}

            <div className="bg-white border-b border-gray-200 py-4 px-4 sm:px-6 lg:px-8 sticky top-20 z-40 shadow-sm">
                <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2 text-sm text-gray-500 truncate">
                        <span className="cursor-pointer hover:text-[#EA723D]" onClick={() => navigate('/')}>Accueil</span>
                        <ChevronRight size={14} />
                        <span className="cursor-pointer hover:text-[#EA723D]" onClick={() => navigate('/annonces')}>Biens</span>
                        <ChevronRight size={14} />
                        <span className="font-semibold text-[#1A2A3A] truncate">{property.title}</span>
                    </div>
                    <button
                        onClick={() => setIsShareModalOpen(true)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-bold uppercase tracking-wider text-[#1A2A3A] hover:bg-gray-50 transition-colors shrink-0"
                    >
                        <Share2 size={14} className="text-[#EA723D]" /> Partager
                    </button>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="grid lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2 space-y-6">
                        <div className="bg-white p-2 shadow-sm rounded-2xl overflow-hidden border border-gray-100">
                            {/* Main Image */}
                            <div className="relative aspect-video rounded-xl overflow-hidden mb-2 bg-gray-100 cursor-pointer group" onClick={openLightbox}>
                                <img
                                    src={getOptimizedImageUrl(selectedImage || property.image_url || property.image, 1200)}
                                    alt={property.title}
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                />
                            </div>

                            {/* Gallery */}
                            {allImages.length > 1 && (
                                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                                    {allImages.map((img, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => setSelectedImage(img)}
                                            className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${currentImageSrc === img ? 'border-[#EA723D]' : 'border-transparent opacity-70 hover:opacity-100'}`}
                                        >
                                            <img src={getOptimizedImageUrl(img, 200)} alt={`Vue ${idx + 1}`} className="w-full h-full object-cover" />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="bg-white p-6 md:p-8 shadow-sm rounded-2xl border border-gray-100">
                            <div className="flex justify-between items-start mb-8 gap-4">
                                <div>
                                    <h1 className="text-2xl md:text-3xl font-black text-[#1A2A3A] leading-tight mb-2">{property.title}</h1>
                                    <p className="text-gray-500 flex items-center gap-1 font-medium"><MapPin size={18} className="text-[#EA723D]" /> {property.city} ({property.zip_code || ''})</p>
                                </div>
                                <div className="text-right">
                                    <div className="text-2xl md:text-3xl font-black text-[#1A2A3A]">{formatPrice(property.price)}</div>
                                    <span className={`inline-block text-white text-[10px] font-bold px-3 py-1 uppercase rounded-full shadow-md mt-2 ${property.status === 'Location' ? 'bg-[#EA723D]' : 'bg-[#1A2A3A]'}`}>
                                        {property.status}
                                    </span>
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-3 mb-10">
                                <div className="text-center p-3 bg-[#F7F7F7] rounded-xl">
                                    <HomeIcon size={20} className="mx-auto text-[#EA723D] mb-1" />
                                    <span className="block text-sm font-black text-[#1A2A3A] truncate">{property.type}</span>
                                    <span className="text-[10px] text-gray-400 uppercase font-bold">Type</span>
                                </div>
                                <div className="text-center p-3 bg-[#F7F7F7] rounded-xl">
                                    <Maximize2 size={20} className="mx-auto text-[#EA723D] mb-1" />
                                    <span className="block text-sm font-black text-[#1A2A3A]">{property.surface || property.surface_terrain || '-'} m²</span>
                                    <span className="text-[10px] text-gray-400 uppercase font-bold">Surface</span>
                                </div>
                                <div className="text-center p-3 bg-[#F7F7F7] rounded-xl">
                                    <BedDouble size={20} className="mx-auto text-[#EA723D] mb-1" />
                                    <span className="block text-sm font-black text-[#1A2A3A]">{property.rooms || property.nb_pieces || '-'}</span>
                                    <span className="text-[10px] text-gray-400 uppercase font-bold">Pièces</span>
                                </div>
                            </div>

                            <h3 className="font-bold text-xl mb-4 text-[#2C353D]">Description</h3>
                            <div className="text-gray-600 leading-relaxed mb-10 text-justify whitespace-pre-line text-base md:text-lg">
                                {property.description}
                            </div>

                            {/* DPE Indicators */}
                            {(property.dpe_energy || property.dpe_ges) && (
                                <>
                                    <h3 className="font-bold text-xl mb-6 text-[#2C353D]">Performance Énergétique</h3>
                                    <div className="grid grid-cols-2 gap-4 mb-10">
                                        <div className="bg-[#F8F9FA] p-4 rounded-xl border border-gray-100 flex flex-col items-center">
                                            <span className="text-[10px] font-bold text-gray-400 uppercase mb-2">Classe Énergie</span>
                                            <div className={`w-12 h-12 flex items-center justify-center rounded-lg text-white font-black text-xl shadow-lg ${property.dpe_energy === 'A' ? 'bg-green-500' : property.dpe_energy === 'B' ? 'bg-green-400' : property.dpe_energy === 'C' ? 'bg-yellow-400' : property.dpe_energy === 'D' ? 'bg-orange-400' : 'bg-red-500'}`}>
                                                {property.dpe_energy || '?'}
                                            </div>
                                        </div>
                                        <div className="bg-[#F8F9FA] p-4 rounded-xl border border-gray-100 flex flex-col items-center">
                                            <span className="text-[10px] font-bold text-gray-400 uppercase mb-2">Classe Climat</span>
                                            <div className={`w-12 h-12 flex items-center justify-center rounded-lg text-white font-black text-xl shadow-lg ${property.dpe_ges === 'A' ? 'bg-purple-300' : 'bg-purple-600'}`}>
                                                {property.dpe_ges || '?'}
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            <h3 className="font-bold text-xl mb-4 text-[#1A2A3A]">Les points clés</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {property.features ? property.features.map((f, i) => (
                                    <div key={i} className="flex items-center gap-3 p-3 bg-[#F7F7F7] rounded-xl text-sm font-bold text-gray-700">
                                        <CheckCircle2 size={18} className="text-[#EA723D] shrink-0" /> {f}
                                    </div>
                                )) : (
                                    <p className="text-gray-400 text-sm italic">Consultez-nous pour plus d'informations.</p>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="space-y-6">
                        <div className="bg-white p-8 shadow-xl rounded-2xl sticky top-36 border-t-4 border-[#1A2A3A]">
                            <div className="flex items-center gap-4 mb-8">
                                <img src={AGENCY_INFO.photo} className="w-20 h-20 rounded-full object-cover border-4 border-[#F7F7F7] shadow-lg" alt={AGENCY_INFO.name} />
                                <div>
                                    <p className="font-black text-[#1A2A3A] text-xl leading-tight">{AGENCY_INFO.name}</p>
                                    <p className="text-xs text-[#EA723D] font-bold uppercase tracking-wide mt-1">
                                        {AGENCY_INFO.role}
                                    </p>
                                </div>
                            </div>

                            {/* Agent Bio / Description */}
                            <p className="text-gray-500 text-xs italic mb-6 leading-relaxed border-l-2 border-[#EA723D]/20 pl-4">
                                Avantis Immobilier - Agence de référence au Luxembourg.
                            </p>

                            <div className="space-y-3">
                                <Button
                                    variant="custom"
                                    className="w-full bg-[#1A2A3A] hover:bg-[#121d28] text-white flex items-center justify-center gap-3 py-4 rounded-xl text-sm font-bold transition-all hover:scale-[1.02]"
                                    onClick={() => {
                                        trackContactClick('phone', property.title);
                                        window.location.href = `tel:${(agent?.phone || AGENCY_INFO.phone).replace(/\s/g, '')}`;
                                    }}
                                >
                                    <Phone size={18} /> {agent?.phone || AGENCY_INFO.phone}
                                </Button>
                                <Button
                                    variant="custom"
                                    onClick={() => {
                                        trackContactClick('email_page', property.title);
                                        navigate('/contact', { state: { propertyTitle: property.title } });
                                    }}
                                    className="w-full flex items-center justify-center gap-3 py-4 rounded-xl text-sm bg-white border-2 border-gray-100 hover:border-gray-200 text-[#2C353D] font-bold transition-all hover:scale-[1.02]"
                                >
                                    <Mail size={18} /> ENVOYER UN EMAIL
                                </Button>
                                <Button
                                    variant="custom"
                                    className="w-full bg-[#25D366] hover:bg-[#20bd5a] text-white flex items-center justify-center gap-3 py-4 rounded-xl text-sm font-bold transition-all hover:scale-[1.02]"
                                    onClick={() => {
                                        trackContactClick('whatsapp', property.title);
                                        const phoneRaw = (AGENCY_INFO.whatsapp || AGENCY_INFO.phone).replace(/\s/g, '').replace('+', '');
                                        const message = `Bonjour Anne-Catherine, Je souhaiterais avoir des informations sur le bien : ${property.title} (${property.city})`;
                                        window.open(`https://wa.me/${phoneRaw}?text=${encodeURIComponent(message)}`, '_blank');
                                    }}
                                >
                                    <MessageCircle size={18} /> WHATSAPP
                                </Button>
                                <Button
                                    variant="custom"
                                    className="w-full bg-gray-100 hover:bg-gray-200 text-[#2C353D] flex items-center justify-center gap-3 py-4 rounded-xl text-sm font-bold transition-all hover:scale-[1.02]"
                                    onClick={() => setIsShareModalOpen(true)}
                                >
                                    <Share2 size={18} /> PARTAGER & QR CODE
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <ShareModal
                isOpen={isShareModalOpen}
                onClose={() => setIsShareModalOpen(false)}
                propertyTitle={property.title}
                propertyUrl={window.location.href}
            />
        </div>
    );
};

export default PropertyDetail;
